'use client'

import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'

import type { UserAction, UserActionType } from '../types'

const DEBUG_PANEL_SELECTOR = '[data-debug-panel]'

interface UseUserActionTrackerOptions {
	onAction: (action: Omit<UserAction, 'id' | 'timestamp'>) => void
	maxActions?: number
}

export const useUserActionTracker = ({ onAction, maxActions: _maxActions = 50 }: UseUserActionTrackerOptions): void => {
	const pathname = usePathname()
	const prevPathRef = useRef<string>(pathname)

	const trackAction = useCallback(
		(type: UserActionType, target: string, details?: Record<string, unknown>) => {
			onAction({ type, target, details })
		},
		[onAction]
	)

	const handleClick = useCallback(
		(event: MouseEvent) => {
			const target = event.target as HTMLElement
			if (!target) return

			// Ignore clicks on debug panel
			if (target.closest(DEBUG_PANEL_SELECTOR)) return

			const tagName = target.tagName.toLowerCase()
			const id = target.id ? `#${target.id}` : ''
			const className =
				target.className && typeof target.className === 'string'
					? `.${target.className.split(' ').filter(Boolean).join('.')}`
					: ''
			const text = target.textContent?.slice(0, 30) || ''

			trackAction('click', `${tagName}${id}${className}`, {
				text: text.trim(),
				x: event.clientX,
				y: event.clientY
			})
		},
		[trackAction]
	)

	const handleSubmit = useCallback(
		(event: SubmitEvent) => {
			const form = event.target as HTMLFormElement
			if (!form) return

			const formId = form.id ? `#${form.id}` : ''
			const formName = form.name ? `[name="${form.name}"]` : ''
			const action = form.action || 'unknown'

			trackAction('submit', `form${formId}${formName}`, {
				action,
				method: form.method
			})
		},
		[trackAction]
	)

	useEffect(() => {
		if (pathname !== prevPathRef.current) {
			trackAction('navigation', pathname, { type: 'routeChange' })
			prevPathRef.current = pathname
		}
	}, [pathname, trackAction])

	useEffect(() => {
		if (typeof window === 'undefined') return

		document.addEventListener('click', handleClick, { capture: true })
		document.addEventListener('submit', handleSubmit, { capture: true })

		return () => {
			document.removeEventListener('click', handleClick, { capture: true })
			document.removeEventListener('submit', handleSubmit, { capture: true })
		}
	}, [handleClick, handleSubmit])
}
